import { InputHTMLAttributes, useState } from 'react'
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SvgIcon,
  Typography
} from '@mui/material'

import { isHappa } from '@/hooks/use-brand'

export type SearchBarProps = InputHTMLAttributes<HTMLInputElement> & {
  sx?: object
  onSearch?: (_value: string) => void
  selectOptions?: { value: string; label: string }[]
  selectLabel?: string
  selectPlaceholder?: string
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  sx,
  selectOptions,
  selectLabel,
  selectPlaceholder,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectValue, setSelectValue] = useState('')

  const handleSearch = () => {
    onSearch?.(searchValue)
  }

  return (
    <Box
      sx={(theme) => ({
        borderRadius: theme.shape.borderRadius360,
        backgroundColor: theme.palette.text.primaryContrastText,
        p: theme.spacing(1),
        pl: theme.spacing(3),
        boxShadow: theme.shadows[2],
        ...sx
      })}
      {...props}
    >
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            alignItems: 'flex-start'
          }}
        >
          {selectLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '1.2rem', color: '#9E9E9E' }}
            >
              {selectLabel}
            </Typography>
          ) : null}
          {selectOptions && selectOptions.length > 0 && (
            <FormControl fullWidth sx={{ width: '100%' }}>
              <Select
                value={selectValue}
                displayEmpty
                onChange={(e) => {
                  setSelectValue(e.target.value)
                  setSearchValue?.(e.target.value)
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <em style={{ fontStyle: 'normal', opacity: 0.5 }}>{selectPlaceholder}</em>
                    )
                  }
                  return selectOptions.find((o) => o.value === selected)?.label || selected
                }}
                variant="standard"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  textAlign: 'left',
                  '& .MuiSelect-select': {
                    pt: 0
                  }
                }}
                IconComponent={({ open }) => (
                  <SvgIcon
                    fontSize="small"
                    sx={{ fill: '#9E9E9E' }}
                    style={{
                      transform: open ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <use href="#ico-arrow-down" />
                  </SvgIcon>
                )}
              >
                {selectOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Divider
          sx={() => ({
            width: '0.1rem',
            height: '3.2rem',
            backgroundColor: 'rgba(0, 0, 0, 0.12)'
          })}
        />
        <IconButton size="large" color={isHappa ? 'accent' : 'secondary'} onClick={handleSearch}>
          <SvgIcon fontSize="small">
            <use href="#ico-search" />
          </SvgIcon>
        </IconButton>
      </Box>
    </Box>
  )
}

export default SearchBar
